/**
 * Shared validation and error parsing for site modals.
 */

const USER_FRIENDLY_MESSAGES: Record<string, string> = {
  "This field may not be blank.": "This field is required.",
  "This field is required.": "This field is required.",
  "This field may not be null.": "This field is required.",
  "A valid number is required.": "Please enter a valid number.",
  "A valid integer is required.": "Please enter a valid number.",
  "Ensure this value is less than or equal to 90.":
    "Latitude must be between -90 and 90.",
  "Ensure this value is greater than or equal to -90.":
    "Latitude must be between -90 and 90.",
  "Ensure this value is less than or equal to 180.":
    "Longitude must be between -180 and 180.",
  "Ensure this value is greater than or equal to -180.":
    "Longitude must be between -180 and 180.",
};

/** Patterns for polygon/geometry errors only. */
const POLYGON_ERROR_PATTERNS: [RegExp, string][] = [
  [/GEOS|WKTReader|closing rings/i, "Invalid polygon format. Please check the geometry and ensure all rings are properly closed."],
  [/Error updating neighbors/i, "Invalid polygon. Please verify the geometry format and closing rings."],
  [/Invalid geometry/i, "Invalid polygon format. Please verify the geometry and closing rings."],
];

const LATITUDE_ERROR_PATTERNS: [RegExp, string][] = [
  [/Ensure this value is less than or equal to/i, "Latitude must be between -90 and 90."],
  [/Ensure this value is greater than or equal to/i, "Latitude must be between -90 and 90."],
];

const LONGITUDE_ERROR_PATTERNS: [RegExp, string][] = [
  [/Ensure this value is less than or equal to/i, "Longitude must be between -180 and 180."],
  [/Ensure this value is greater than or equal to/i, "Longitude must be between -180 and 180."],
];

function toUserFriendlyMessage(
  msg: string,
  isPolygon = false,
  field?: string
): string {
  if (USER_FRIENDLY_MESSAGES[msg]) return USER_FRIENDLY_MESSAGES[msg];
  if (field === "latitude") {
    for (const [pattern, friendly] of LATITUDE_ERROR_PATTERNS) {
      if (pattern.test(msg)) return friendly;
    }
  }
  if (field === "longitude") {
    for (const [pattern, friendly] of LONGITUDE_ERROR_PATTERNS) {
      if (pattern.test(msg)) return friendly;
    }
  }
  if (isPolygon) {
    for (const [pattern, friendly] of POLYGON_ERROR_PATTERNS) {
      if (pattern.test(msg)) return friendly;
    }
  }
  return msg;
}

/** Converts backend geometry/polygon errors to user-friendly messages. */
export function toUserFriendlyPolygonError(msg: string): string {
  return toUserFriendlyMessage(msg, true);
}

export function validateSiteForm(
  formData: Record<string, unknown>,
  requiredFields: readonly string[],
  numericFields: string[] = []
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const field of requiredFields) {
    const value = formData[field];
    if (value == null || value === "") {
      errors[field] = "This field is required.";
    }
  }

  for (const field of numericFields) {
    const value = formData[field];
    const hasValue = value != null && value !== "";
    if (hasValue && isNaN(Number(value))) {
      errors[field] = "Please enter a valid number.";
    }
  }

  return errors;
}

const normalizeSamId = (samId: unknown) =>
  String(samId ?? "")
    .trim()
    .toLowerCase();

export function isSamIdUnchanged(
  nextSamId: unknown,
  originalSamId: unknown
): boolean {
  return normalizeSamId(nextSamId) === normalizeSamId(originalSamId);
}

const normalizeSiteId = (siteId: unknown) => {
  if (siteId == null || siteId === "") return "";
  const asString = String(siteId).trim();
  const asNumber = Number(asString);
  return Number.isFinite(asNumber) ? String(asNumber) : asString;
};

const getSiteId = (site: Record<string, unknown>) => {
  if (site.id != null) return normalizeSiteId(site.id);
  if (typeof site.url === "string") {
    return normalizeSiteId(site.url.match(/\/sites\/(\d+)\//)?.[1] ?? "");
  }
  return "";
};

export function hasDuplicateSamId(
  sites: Record<string, unknown>[] | undefined,
  samId: unknown,
  currentSiteId?: unknown
): boolean {
  const normalizedSamId = normalizeSamId(samId);
  if (!normalizedSamId || !sites) return false;

  const normalizedCurrentSiteId = normalizeSiteId(currentSiteId);

  return sites.some((site) => {
    const siteId = getSiteId(site);
    if (normalizedCurrentSiteId && siteId === normalizedCurrentSiteId) {
      return false;
    }
    return normalizeSamId(site.sam_id) === normalizedSamId;
  });
}

export function parseBackendValidationErrors(
  error: unknown
): Record<string, string> | null {
  const err = error as { response?: { status?: number; data?: unknown } };
  const data = err?.response?.status === 400 && err?.response?.data;
  if (!data) return null;

  if (typeof data === "string")
    return { detail: toUserFriendlyMessage(data, true) };

  if (typeof data !== "object") return null;

  const obj = data as Record<string, unknown>;
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => {
      const raw = Array.isArray(v) ? v[0] : String(v);
      const field = k === "poly" ? "polygon" : k;
      const value = toUserFriendlyMessage(raw, field === "polygon", field);
      return [field, value];
    })
  );
}
